/**
 * Vue composable — the ergonomic surface most components should use.
 *
 * Usage in a component that hosts a tour:
 *
 *   import { useTour } from '@/tour';
 *   // in mounted():
 *   this.$nextTick(() => useTour().autoStart('announcements'));
 *
 * Or to wire a "Replay tour" / "Take the tour" button:
 *
 *   const { start, restart } = useTour();
 *   start('announcements');       // respects `enabled`
 *   restart('announcements');     // clears "seen" and forces it
 */

import { tourManager } from '../core/tourManager';

export function useTour() {
  return {
    /** Auto-launch if allowed (enabled ∧ scheduled ∧ unseen ∧ not View-As). */
    autoStart: (id: string) => tourManager.autoStart(id),
    /**
     * Permanent auto-start eligibility, ignoring the transient open-dialog block.
     * Use as a modal-queue `canShow`; autoStart() still applies the transient block
     * (and defers) once the tour becomes current.
     */
    isAutoStartEligible: (id: string) => tourManager.isAutoStartEligible(id),
    /** Manually start (respects `enabled`; ignores "seen"). */
    start: (id: string) => tourManager.start(id),
    /** Clear "seen" and force from the top. */
    restart: (id: string) => tourManager.restart(id),
    /** Stop any running tour. */
    stop: () => tourManager.stop(),
    /** Whether the tour is enabled in the current environment. */
    isEnabled: (id: string) => tourManager.isEnabled(id)
  };
}
